import type { FormEvent } from "react";
import LoadingLabel from "../../components/LoadingLabel";

// A dropdown whose last option switches it into a field for adding a new choice in place, as the
// entry form does for categories and subcategories. The caller owns the state and the save.

const ADD_NEW = "__add_new__";

export default function InlineAddSelect({
  value, options, onSelect, emptyLabel, emptyDisabled = false, required = false, ariaLabel, addOptionLabel,
  adding, onStartAdding, name, onName, nameLabel, busy, onAdd, addLabel, onStop, onCancel,
}: {
  value: string;
  options: string[];
  onSelect: (value: string) => void;
  /** The blank first option: a prompt, or the explicit "none" choice. */
  emptyLabel: string;
  emptyDisabled?: boolean;
  required?: boolean;
  ariaLabel?: string;
  /** The last option, which opens the add field instead of selecting anything. */
  addOptionLabel: string;
  adding: boolean;
  onStartAdding: () => void;
  name: string;
  onName: (name: string) => void;
  /** Both the new-name field's label and its placeholder. */
  nameLabel: string;
  busy: boolean;
  /** Receives the click, which the form's own handlers prevent from submitting it. */
  onAdd: (event: FormEvent) => void;
  addLabel: string;
  /** Escape: leave the add field. */
  onStop: () => void;
  /** Cancel: leave the add field and clear any error it raised. */
  onCancel: () => void;
}) {
  if (adding)
    return (
      <span className="option-add-inline">
        <input
          autoFocus
          aria-label={nameLabel}
          value={name}
          onChange={(e) => onName(e.target.value)}
          placeholder={nameLabel}
          maxLength={80}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              // Handled here: leaves the add field without closing the sheet around it.
              e.preventDefault();
              onStop();
            }
          }}
        />
        <span className="option-add-actions">
          <button type="button" disabled={busy || !name.trim()} onClick={onAdd}>
            {busy ? <LoadingLabel>Adding</LoadingLabel> : addLabel}
          </button>
          <button type="button" className="text-button" onClick={onCancel}>
            Cancel
          </button>
        </span>
      </span>
    );
  return (
    <select
      required={required}
      value={value}
      aria-label={ariaLabel}
      onChange={(e) => {
        if (e.target.value === ADD_NEW) {
          onStartAdding();
          return;
        }
        onSelect(e.target.value);
      }}
    >
      <option value="" disabled={emptyDisabled}>
        {emptyLabel}
      </option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
      <option value={ADD_NEW}>{addOptionLabel}</option>
    </select>
  );
}
